/* eslint-disable no-console */
import { Component, OnInit, Input, forwardRef } from '@angular/core';
import {
	DATE_TIME_FORMATTING,
	DATE_FORMATTING,
	fileDataTypesMapUI,
	ModalService,
	ErrorCodes,
	DateTimeService,
	TIME12_FORMATTING,
	TIME24_FORMATTING
} from '@shared';
import { PickerOptions } from '@ionic/core';
import { ControlValueAccessor, UntypedFormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FormControlDatePicker } from '../../controls/form-control-datepicker';
import { format, formatISO } from 'date-fns';
import { DatePickerModalComponent } from '../date-picker-modal';
import { DatePickerHourCycleTypeEnum, DatePickerPresentationTypeEnum, DatePickerRoleTypeEnum } from '../../models';
import { LoggingService } from '@shared/logging';

@Component({
	selector: 'app-date-picker',
	templateUrl: './date-picker.component.html',
	styleUrls: ['./date-picker.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => DatePickerComponent),
			multi: true
		}
	]
})
export class DatePickerComponent implements OnInit, ControlValueAccessor {
	@Input() public dynamicFormControl: FormControlDatePicker;
	@Input() public form: UntypedFormGroup;
	public dateDisplayFormat: string;
	public customPickerOptions: Partial<PickerOptions>;
	public showDatetimePicker = true;
	public displayDate: string;

	public constructor(
		private modalService: ModalService,
		private loggingService: LoggingService,
		private dateTimeService: DateTimeService
	) {}

	public writeValue(dateValue: any): void {
		if (dateValue) {
			this.displayDate = format(new Date(dateValue), this.dateDisplayFormat);

		}
	}
	public registerOnChange(fn: any): void {
		this.propagateChange = fn;
	}
	/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
	public registerOnTouched(fn: any): void {}
	/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
	public setDisabledState?(isDisabled: boolean): void {}

	public ngOnInit() {
		
		this.dateDisplayFormat =
			this.dynamicFormControl.type === fileDataTypesMapUI.DATE ? DATE_FORMATTING : DATE_TIME_FORMATTING 
			
	}

	public async openModal() {
		try {
			await this.modalService.createAndShow(
				DatePickerModalComponent,
				'datetime-picker',
				{
					date: this.getDate(this.dynamicFormControl.type),
					presentationType: this.getPresentationType(this.dynamicFormControl.type),
					hourCycleType:
						this.dynamicFormControl.type === fileDataTypesMapUI.TIME12
							? DatePickerHourCycleTypeEnum.H12
							: DatePickerHourCycleTypeEnum.H24
				},
				true
			);

			const response = await this.modalService.onDidDismiss();
			

			if (response.role === DatePickerRoleTypeEnum.Cancel) {
				this.displayDate = null;
			}

			if (response.role === DatePickerRoleTypeEnum.Save && response.data !== undefined) {

				this.displayDate = format(new Date(response.data.date), this.dateDisplayFormat);

			}

			this.form.get(this.dynamicFormControl.key).markAsDirty();
			
			this.propagateChange(this.displayDate);
		} catch (error) {
			this.loggingService.error(ErrorCodes.DatePickerHandler, error);
			throw new Error('datetime picker failed');
		}
	}

	public getIconClass(): string {
		let iconClass = 'icon-Datepicker';

		if (this.controlTypeIsTimeField(this.dynamicFormControl.type)) {
			iconClass = 'icon-Schedule';
		}

		return this.dynamicFormControl.disabled ? `${iconClass} disabled-class` : iconClass;
	}

	public getDateFormat(controlType: string): string {
		if (controlType === fileDataTypesMapUI.TIME12) {
			return TIME12_FORMATTING;
		}

		if (controlType === fileDataTypesMapUI.TIME24) {
			return TIME24_FORMATTING;
		}

		return controlType === fileDataTypesMapUI.DATE ? DATE_FORMATTING : DATE_TIME_FORMATTING;
	}

	private getDate(controlType: string): string {
		if (this.displayDate) {
			return formatISO(new Date(this.displayDate));
		}

		if (this.controlTypeIsTimeField(controlType)) {
			return formatISO(this.dateTimeService.getEpochDate());
		}

		return formatISO(this.dateTimeService.getCurrentDateTime());
	}

	private getPresentationType(controlType: string): DatePickerPresentationTypeEnum {
		if (this.controlTypeIsTimeField(controlType)) {
			return DatePickerPresentationTypeEnum.Time;
		}

		return this.dynamicFormControl.type === fileDataTypesMapUI.DATE
			? DatePickerPresentationTypeEnum.Date
			: DatePickerPresentationTypeEnum.DateTime;
	}

	private controlTypeIsTimeField(controlType: string): boolean {
		return [fileDataTypesMapUI.TIME12, fileDataTypesMapUI.TIME24].includes(controlType);
	}

	/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
	private propagateChange = (_: any) => {};
}
